import { useState, type ComponentProps } from 'react';
import { Loader2, Mail } from 'lucide-react';
import QRCode from 'react-qr-code';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '~/components/ui/alert-dialog';
import { Button } from '~/components/ui/button';

type ButtonProps = ComponentProps<typeof Button>;

type SendAnamneseUrlDialogProps = {
    customerId: number;
    appointmentId?: string;
    triggerClassName?: string;
    triggerVariant?: ButtonProps['variant'];
    triggerSize?: ButtonProps['size'];
};

export const SendAnamneseUrlDialog = ({ customerId, appointmentId, triggerClassName, triggerVariant = 'outline', triggerSize = 'sm' }: SendAnamneseUrlDialogProps) => {
    const [open, setOpen] = useState(false);

    const anamneseUrlQuery = trpc.customers.getCustomerAnamneseUrl.useQuery(
        { customerId },
        {
            enabled: !!customerId && open,
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000,
        }
    );

    const sendAnamneseUrlMutation = trpc.customers.sendAnamneseUrl.useMutation({
        onSuccess: () => {
            toast.success("Anamnese-Link erfolgreich gesendet.");
        },
        onError: (error) => {
            toast.error("Fehler beim Senden des Anamnese-Links.", {
                description: error.message,
            });
        },
    });

    return (
        <AlertDialog open={open} onOpenChange={setOpen}>
            <AlertDialogTrigger asChild>
                <Button
                    size={triggerSize}
                    className={triggerClassName}
                    variant={triggerVariant}
                    disabled={sendAnamneseUrlMutation.isPending}
                >
                    {sendAnamneseUrlMutation.isPending ? <Loader2 className='animate-spin' /> : <Mail />}
                    Anamnese-Link senden
                </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Bist du dir sicher?</AlertDialogTitle>
                    <AlertDialogDescription>
                        Bist du sicher, dass du die Anamnese-URL senden möchtest?
                    </AlertDialogDescription>
                </AlertDialogHeader>

                <div className="rounded-md border p-3 bg-muted/30">
                    <div className="text-xs text-muted-foreground mb-2">Direkter Anamnese-Link</div>
                    {anamneseUrlQuery.isLoading && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin text-primary-400" />
                            Lade Link...
                        </div>
                    )}
                    {anamneseUrlQuery.isError && (
                        <div className="text-sm text-destructive">Link konnte nicht geladen werden.</div>
                    )}
                    {anamneseUrlQuery.data?.url && (
                        <div className="flex flex-col items-start gap-3">
                            <div className="p-3 bg-white rounded-md border">
                                <QRCode
                                    value={anamneseUrlQuery.data.url}
                                    size={160}
                                    style={{ height: "auto", maxWidth: "100%", width: "160px" }}
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <a
                                    href={anamneseUrlQuery.data.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="truncate text-primary underline max-w-[22rem]"
                                    title={anamneseUrlQuery.data.url}
                                >
                                    {anamneseUrlQuery.data.url}
                                </a>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={async () => {
                                        await navigator.clipboard.writeText(anamneseUrlQuery.data!.url);
                                        toast.success("Link wurde kopiert")
                                    }}
                                    className="h-7 px-2 text-xs"
                                >
                                    Copy
                                </Button>
                            </div>
                        </div>
                    )}
                </div>

                <AlertDialogFooter>
                    <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                    <AlertDialogAction asChild>
                        <Button
                            onPointerDown={(e) => {
                                e.stopPropagation();
                            }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                sendAnamneseUrlMutation.mutate({ customerId, appointmentId });
                            }}
                            disabled={sendAnamneseUrlMutation.isPending}
                        >
                            {sendAnamneseUrlMutation.isPending && <Loader2 className='animate-spin' />}
                            Anamnese-Link senden
                        </Button>
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};
